(() => {
  'use strict';

  /* ---------- header intro reveal ---------- */
  requestAnimationFrame(() => {
    requestAnimationFrame(() => document.body.classList.add('jscoorde_loaded'));
  });

  /* ---------- scroll reveal for images / text blocks ---------- */
  const revealTargets = document.querySelectorAll('[data-jscoorde-animate]');
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('jscoorde_animate');
        revealObserver.unobserve(entry.target);
      }
    });
  }, { threshold: 0.22, rootMargin: '0px 0px -8% 0px' });
  revealTargets.forEach((el) => revealObserver.observe(el));

  /* ---------- auto-rotating photo slider (clip-path wipe) ---------- */
  const DIRS = ['jscoorde_u_r', 'jscoorde_u_l', 'jscoorde_u_t', 'jscoorde_u_b', 'jscoorde_u_c'];
  const sliderControls = new Map(); // .jscoorde_js_slide container -> { advance, restart }

  const WIPE_MS = 1100;

  function initSlider(container) {
    const slides = Array.from(container.querySelectorAll('.jscoorde_slide'));
    if (slides.length < 2) return;

    const indicator = container.parentElement.querySelector('.jscoorde_slide_indicator');

    // track the active slide ourselves instead of re-deriving it from
    // classList each time — querying the DOM for "which one is active" is
    // what let this drift out of sync when a cycle's cleanup was still
    // pending, which is what caused the first-photo return to sometimes
    // skip its wipe entirely.
    let activeIndex = Math.max(0, slides.findIndex((s) => s.classList.contains('jscoorde_active')));
    let timer = null;
    let cleanupTimer = null;
    let pending = null; // { prev, next } for the cycle currently mid-wipe, or null

    const finishCleanup = () => {
      if (!pending) return;
      clearTimeout(cleanupTimer);
      const { prev, next, onEnd } = pending;
      if (onEnd) next.removeEventListener('transitionend', onEnd);
      prev.style.transition = 'none';
      prev.classList.remove('jscoorde_active');
      void prev.offsetWidth;
      prev.style.transition = '';
      const prevVideo = prev.querySelector('video');
      if (prevVideo) prevVideo.pause();
      next.classList.remove('jscoorde_incoming');
      pending = null;
    };

    const advance = (onSwitched) => {
      // self-heal: if the previous cycle's cleanup timer never fired (e.g.
      // the tab was backgrounded and browsers throttle/drop timers there),
      // finish it right now instead of leaving this slider stuck forever —
      // otherwise every future advance would silently do nothing, which
      // looks exactly like "it stopped animating after landing on photo 1".
      finishCleanup();

      const current = activeIndex;
      const nextIndex = (current + 1) % slides.length;
      const prev = slides[current];
      const next = slides[nextIndex];

      const dir = DIRS[Math.floor(Math.random() * DIRS.length)];

      // snap `next` to its fully-collapsed starting shape instantly, with no
      // transition, so the wipe always starts from a true 0% regardless of
      // whatever state it was left in after its last cycle. Forcing a
      // reflow (offsetWidth) here commits that collapsed shape immediately,
      // which means we can trigger the actual reveal transition right below
      // synchronously — no requestAnimationFrame needed. That extra rAF
      // hop used to be where this broke: if it got delayed or dropped, the
      // slide would sit at its collapsed shape indefinitely, doing nothing
      // until the next full advance blew it away — visually indistinguishable
      // from "there was no wipe at all".
      next.style.transition = 'none';
      DIRS.forEach((d) => next.classList.remove(d));
      next.classList.add(dir);
      void next.offsetWidth; // force the browser to paint the collapsed state
      next.style.transition = '';

      next.classList.add('jscoorde_incoming', 'jscoorde_active');
      activeIndex = nextIndex;
      if (onSwitched) onSwitched(next);

      // pulse the little indicator dot right as the photo starts switching
      if (indicator) {
        indicator.classList.add('jscoorde_pulse');
        setTimeout(() => indicator.classList.remove('jscoorde_pulse'), 500);
      }

      const nextVideo = next.querySelector('video');
      if (nextVideo) {
        nextVideo.currentTime = 0;
        nextVideo.play().catch(() => {});
      }

      // primary completion signal: the wipe's *actual* clip-path transition
      // finishing, whatever that takes in practice on this device — not a
      // guessed duration.
      const onEnd = (e) => {
        if (e.target !== next || e.propertyName !== 'clip-path') return;
        finishCleanup();
      };
      next.addEventListener('transitionend', onEnd);
      pending = { prev, next, onEnd };

      // generous fallback only — covers the rare case transitionend never
      // fires at all (e.g. a backgrounded tab), well clear of normal timing.
      cleanupTimer = setTimeout(finishCleanup, WIPE_MS + 900);

      return next.dataset.jscoordeKind; // 'img' or 'video' — lets callers vary the dwell time
    };

    const dwellFor = (kind) => (kind === 'video' ? 4000 : 3300);

    const start = (isRestart) => {
      if (timer) return;
      // on a fresh restart (e.g. right after a manual flick), respect
      // whatever is currently on screen — a video that was just flicked to
      // should still get its full 5s, not the generic stagger delay.
      const delay = isRestart
        ? dwellFor(slides[activeIndex].dataset.jscoordeKind)
        : 2600 + Math.random() * 1500; // initial stagger so sections don't all wipe in sync
      timer = setTimeout(function loop() {
        const kind = advance();
        timer = setTimeout(loop, dwellFor(kind));
      }, delay);
    };

    const stop = () => {
      clearTimeout(timer);
      timer = null;
    };

    const sliderObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => (entry.isIntersecting ? start() : stop()));
    }, { threshold: 0.15 });
    sliderObserver.observe(container);

    sliderControls.set(container, {
      advance,
      restart: () => { stop(); start(true); },
    });
  }

  document.querySelectorAll('.jscoorde_js_slide').forEach(initSlider);

  /* ---------- flick/swipe detection ----------
     fires onSwipe(dx) for a quick, mostly-horizontal drag;
     ignores slow drags and taps so clicks still work normally. */
  function onFlick(el, onSwipe) {
    let sx = 0, sy = 0, st = 0;
    el.addEventListener('touchstart', (e) => {
      const t = e.touches[0];
      sx = t.clientX; sy = t.clientY; st = Date.now();
    }, { passive: true });
    el.addEventListener('touchend', (e) => {
      const t = e.changedTouches[0];
      const dx = t.clientX - sx;
      const dy = t.clientY - sy;
      const dt = Date.now() - st;
      if (Math.abs(dx) > 36 && Math.abs(dx) > Math.abs(dy) * 1.4 && dt < 600) {
        onSwipe(dx);
      }
    }, { passive: true });
  }

  /* ---------- parallax ----------
     these elements own their `transform` exclusively (no CSS
     transition/translateY reveal competes with it) so JS can
     just overwrite it every frame without fighting other styles. */
  const parallaxEls = Array.from(document.querySelectorAll('[data-jscoorde-parallax]')).map((el) => ({
    el,
    factor: parseFloat(el.dataset.jscoordeParallax) || 0,
  }));
  parallaxEls.forEach(({ el }) => { el.style.willChange = 'transform'; });

  let ticking = false;
  function applyParallax() {
    const vh = window.innerHeight;
    parallaxEls.forEach(({ el, factor }) => {
      const rect = el.getBoundingClientRect();
      const center = rect.top + rect.height / 2 - vh / 2;
      const offset = (center * factor * -1).toFixed(1);
      el.style.transform = `translate3d(0, ${offset}px, 0)`;
    });
    ticking = false;
  }
  function onScroll() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(applyParallax);
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  applyParallax();

  /* ---------- lightbox: click a photo to view it large ---------- */
  const lightbox = document.getElementById('jscoorde_lightbox');
  const lightboxImg = document.getElementById('jscoorde_lightbox_img');
  const lightboxVideo = document.getElementById('jscoorde_lightbox_video');
  const lightboxClose = lightbox.querySelector('.jscoorde_lightbox_close');
  let lastFocused = null;
  let currentContainer = null; // the .jscoorde_js_slide currently shown in the lightbox

  function setLightboxMedia(mediaEl) {
    lightboxImg.classList.remove('jscoorde_is_visible');
    lightboxVideo.classList.remove('jscoorde_is_visible');
    lightboxVideo.pause();

    if (mediaEl.tagName === 'VIDEO') {
      lightboxVideo.src = mediaEl.currentSrc || mediaEl.src;
      lightboxVideo.classList.add('jscoorde_is_visible');
      lightboxVideo.play().catch(() => {});
    } else {
      lightboxImg.src = mediaEl.currentSrc || mediaEl.src;
      lightboxImg.alt = mediaEl.alt || '';
      lightboxImg.classList.add('jscoorde_is_visible');
    }
  }

  function openLightbox(mediaEl, container) {
    setLightboxMedia(mediaEl);
    currentContainer = container;

    lastFocused = document.activeElement;
    lightbox.classList.add('jscoorde_is_open');
    lightbox.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    lightboxClose.focus();
  }

  function showNext(container, onSwitched) {
    const ctrl = sliderControls.get(container);
    if (!ctrl) return;
    ctrl.advance(onSwitched);
    ctrl.restart();
  }

  function closeLightbox() {
    lightbox.classList.remove('jscoorde_is_open');
    lightbox.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    lightboxVideo.pause();
    lightboxVideo.removeAttribute('src');
    currentContainer = null;
    if (lastFocused) lastFocused.focus();
  }

  document.querySelectorAll('.jscoorde_photo_wrap').forEach((wrap) => {
    wrap.setAttribute('role', 'button');
    wrap.setAttribute('tabindex', '0');
    wrap.setAttribute('aria-label', '写真を拡大表示。フリックで次の写真へ');

    const jsSlide = wrap.querySelector('.jscoorde_js_slide');
    let suppressClick = false;

    const trigger = () => {
      if (suppressClick) return;
      const activeSlide = wrap.querySelector('.jscoorde_slide.jscoorde_active');
      if (!activeSlide) return;
      const media = activeSlide.querySelector('img, video');
      if (media) openLightbox(media, jsSlide);
    };

    wrap.addEventListener('click', trigger);
    wrap.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        trigger();
      }
    });

    // flick the photo itself to jump to the next look, without opening the lightbox
    onFlick(wrap, () => {
      showNext(jsSlide);
      suppressClick = true;
      setTimeout(() => { suppressClick = false; }, 300);
    });
  });

  // flick inside the lightbox to advance to the next photo of the same look
  onFlick(lightbox, () => {
    if (!currentContainer) return;
    showNext(currentContainer, (nextSlide) => {
      const media = nextSlide.querySelector('img, video');
      if (media) setLightboxMedia(media);
    });
  });

  lightboxClose.addEventListener('click', closeLightbox);
  lightbox.addEventListener('click', (e) => {
    if (e.target === lightbox) closeLightbox();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && lightbox.classList.contains('jscoorde_is_open')) closeLightbox();
  });
})();
(function() {
  const targets = [
    document.getElementById('head_menu_wrap'),
    ...document.querySelectorAll('.mobile-only')
  ].filter(Boolean);
  if (!targets.length) return;

  targets.forEach((el) => {
    el.style.transition = 'opacity .4s ease, transform .4s ease';
    el.style.opacity = '0';
    el.style.transform = 'translateY(-100%)';
    el.style.pointerEvents = 'none';
  });

  function htp_toggleHead() {
    const show = window.scrollY > window.innerHeight * 0.6;
    targets.forEach((el) => {
      el.style.opacity = show ? '1' : '0';
      el.style.transform = show ? 'translateY(0)' : 'translateY(-100%)';
      el.style.pointerEvents = show ? '' : 'none';
    });
  }
  window.addEventListener('scroll', htp_toggleHead, { passive: true });
  htp_toggleHead();
})();
